/**
 * What the roster will look like once a change set lands — for showing the
 * admin a before/after, never for deciding legality.
 *
 * Mirrors `roster_fix_apply_operations` closely enough to draw: splits keep
 * the original slot for the earlier half, breaks reuse a slot with the same
 * times, and every touched slot becomes a draft.
 */

import type { BreakSlot, BreakSlotAssignment, DutySlot, DutySlotAssignment } from '../dutyBoardService';
import type { RosterChangeOperation, RosterSnapshot } from './types';

export type GuardNameLookup = (guardId: string) => string;

const PENDING_PREFIX = 'pending:';

export const isPendingId = (id: string): boolean => id.startsWith(PENDING_PREFIX);

function dutyAssignment(guardId: string, name: GuardNameLookup): DutySlotAssignment {
  return { id: `${PENDING_PREFIX}${guardId}`, guard_id: guardId, guard_name: name(guardId), avatar_url: null };
}

function breakAssignment(guardId: string, name: GuardNameLookup): BreakSlotAssignment {
  return { id: `${PENDING_PREFIX}${guardId}`, guard_id: guardId, guard_name: name(guardId), avatar_url: null };
}

const withoutGuard = <T extends { guard_id: string }>(assignments: T[], guardId: string): T[] => (
  assignments.filter((assignment) => assignment.guard_id !== guardId)
);

export function projectOperations(
  snapshot: RosterSnapshot,
  operations: RosterChangeOperation[],
  guardName: GuardNameLookup,
): RosterSnapshot {
  let slots: DutySlot[] = snapshot.slots.map((slot) => ({ ...slot, assignments: [...slot.assignments] }));
  let breakSlots: BreakSlot[] = snapshot.breakSlots.map((slot) => ({ ...slot, assignments: [...slot.assignments] }));

  const updateSlot = (id: string, change: (slot: DutySlot) => DutySlot) => {
    slots = slots.map((slot) => (slot.id === id ? { ...change(slot), is_published: false } : slot));
  };
  const updateBreak = (id: string, change: (slot: BreakSlot) => BreakSlot) => {
    breakSlots = breakSlots.map((slot) => (slot.id === id ? { ...change(slot), is_published: false } : slot));
  };

  operations.forEach((operation) => {
    switch (operation.op) {
      case 'reassign_duty':
        updateSlot(operation.duty_slot_id, (slot) => ({
          ...slot,
          assignments: [...withoutGuard(slot.assignments, operation.from_guard_id), dutyAssignment(operation.to_guard_id, guardName)],
        }));
        break;

      case 'remove_duty':
        updateSlot(operation.duty_slot_id, (slot) => ({ ...slot, assignments: withoutGuard(slot.assignments, operation.guard_id) }));
        break;

      case 'split_duty': {
        const original = slots.find((slot) => slot.id === operation.duty_slot_id);
        if (!original) break;
        const handover = operation.handover_guard_id ? [dutyAssignment(operation.handover_guard_id, guardName)] : [];
        const released = [...withoutGuard(original.assignments, operation.guard_id), ...handover];
        const kept = original.assignments;
        const later: DutySlot = {
          ...original,
          id: `${PENDING_PREFIX}${original.id}:${operation.at_minute}`,
          start_minute: operation.at_minute,
          is_published: false,
          assignments: operation.guard_keeps === 'before' ? released : kept,
        };
        updateSlot(original.id, (slot) => ({
          ...slot,
          end_minute: operation.at_minute,
          assignments: operation.guard_keeps === 'before' ? kept : released,
        }));
        slots = [...slots, later];
        break;
      }

      case 'add_break': {
        const existing = breakSlots.find((slot) => (
          slot.start_minute === operation.start_minute
          && slot.end_minute === operation.end_minute
          && slot.is_paid === operation.is_paid
        ));
        if (existing) {
          updateBreak(existing.id, (slot) => (
            slot.assignments.some((assignment) => assignment.guard_id === operation.guard_id)
              ? slot
              : { ...slot, assignments: [...slot.assignments, breakAssignment(operation.guard_id, guardName)] }
          ));
        } else {
          breakSlots = [...breakSlots, {
            id: `${PENDING_PREFIX}break:${operation.start_minute}-${operation.end_minute}:${operation.is_paid ? 'paid' : 'unpaid'}`,
            company_id: '',
            site_id: snapshot.site.id,
            slot_date: snapshot.slotDate,
            duration_minutes: operation.end_minute - operation.start_minute,
            is_paid: operation.is_paid,
            start_minute: operation.start_minute,
            end_minute: operation.end_minute,
            display_order: breakSlots.length,
            is_published: false,
            notes: null,
            assignments: [breakAssignment(operation.guard_id, guardName)],
          }];
        }
        break;
      }

      case 'transfer_break':
        updateBreak(operation.break_slot_id, (slot) => {
          const remaining = withoutGuard(slot.assignments, operation.from_guard_id);
          return {
            ...slot,
            assignments: remaining.some((assignment) => assignment.guard_id === operation.to_guard_id)
              ? remaining
              : [...remaining, breakAssignment(operation.to_guard_id, guardName)],
          };
        });
        break;

      case 'remove_break':
        updateBreak(operation.break_slot_id, (slot) => ({ ...slot, assignments: withoutGuard(slot.assignments, operation.guard_id) }));
        break;
    }
  });

  return { ...snapshot, slots, breakSlots };
}

/** Guards whose day a change set touches, in first-mentioned order. */
export function affectedGuardIds(operations: RosterChangeOperation[]): string[] {
  const ids: string[] = [];
  const add = (id: string | null | undefined) => {
    if (id && !ids.includes(id)) ids.push(id);
  };
  operations.forEach((operation) => {
    switch (operation.op) {
      case 'reassign_duty':
      case 'transfer_break':
        add(operation.from_guard_id);
        add(operation.to_guard_id);
        break;
      case 'split_duty':
        add(operation.guard_id);
        add(operation.handover_guard_id);
        break;
      default:
        add(operation.guard_id);
    }
  });
  return ids;
}

export interface TimelineSegment {
  key: string;
  type: 'duty' | 'break';
  label: string;
  startMinute: number;
  endMinute: number;
  /** New or changed for this guard compared with the other side of the diff. */
  changed: boolean;
}

/** One guard's duties and breaks on the roster, earliest first. */
export function guardTimeline(snapshot: RosterSnapshot, guardId: string): TimelineSegment[] {
  const duties = snapshot.slots
    .filter((slot) => slot.assignments.some((assignment) => assignment.guard_id === guardId))
    .map((slot): TimelineSegment => ({
      key: `duty:${slot.name}:${slot.start_minute}-${slot.end_minute}`,
      type: 'duty',
      label: slot.name,
      startMinute: slot.start_minute,
      endMinute: slot.end_minute,
      changed: false,
    }));
  const breaks = snapshot.breakSlots
    .filter((slot) => slot.assignments.some((assignment) => assignment.guard_id === guardId))
    .map((slot): TimelineSegment => ({
      key: `break:${slot.start_minute}-${slot.end_minute}`,
      type: 'break',
      label: '',
      startMinute: slot.start_minute,
      endMinute: slot.end_minute,
      changed: false,
    }));
  return [...duties, ...breaks].sort((a, b) => a.startMinute - b.startMinute || a.type.localeCompare(b.type));
}

/** Marks segments that do not appear, with the same times, on the other side. */
export function markChanges(segments: TimelineSegment[], comparedWith: TimelineSegment[]): TimelineSegment[] {
  const otherKeys = new Set(comparedWith.map((segment) => segment.key));
  return segments.map((segment) => ({ ...segment, changed: !otherKeys.has(segment.key) }));
}
